<template>
  <HeaderComponent />
  <main class="relative flex min-h-screen w-full bg-[#f5f7fb] pt-[70px]">
    <SideBarComponent />

    <div class="relative top-0 h-full overflow-x-hidden overflow-y-auto p-[20px] md:p-[25px] lg:left-[250px] lg:w-[calc(100%-250px)] md:left-[78px] md:w-[calc(100%-78px)]">
      <section class="mx-auto w-full max-w-[1200px] space-y-4 pb-10">
        <div class="flex items-center justify-between">
          <h1 class="text-xl font-semibold text-slate-900 md:text-2xl">Gestion des avis</h1>
        </div>

        <div class="rounded-[16px] border border-slate-200 bg-white p-4 shadow-sm md:p-5">
          <h2 class="text-base font-semibold text-slate-900">Envoyer une demande d’avis</h2>

          <form class="mt-4 grid gap-4 md:grid-cols-[1fr_1fr_auto]" @submit.prevent="sendRequest">
            <div>
              <label class="mb-1 block text-sm font-medium text-slate-700">Nom complet</label>
              <input v-model="form.fullName" type="text" class="input" placeholder="Nom du client" required />
            </div>

            <div>
              <label class="mb-1 block text-sm font-medium text-slate-700">Adresse email</label>
              <input v-model="form.email" type="email" class="input" placeholder="client@email.com" required />
            </div>

            <div class="md:self-end">
              <button type="submit" class="btn-primary w-full md:w-auto" :disabled="sendingRequest">
                <span v-if="sendingRequest">Envoi...</span>
                <span v-else>Envoyer</span>
              </button>
            </div>
          </form>
        </div>

        <div class="rounded-[16px] border border-slate-200 bg-white p-4 shadow-sm md:p-5">
          <div class="flex items-center justify-between border-b border-slate-100 pb-3">
            <h2 class="text-base font-semibold text-slate-900">Tous les avis</h2>
            <span class="text-xs text-slate-400">{{ pagination.totalItems }} avis</span>
          </div>

          <div v-if="loading" class="flex items-center justify-center py-14">
            <div class="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-[#D4AF37]"></div>
          </div>

          <div v-else-if="reviews.length === 0" class="py-14 text-center text-sm text-slate-500">
            Aucun avis trouvé.
          </div>

          <div v-else class="space-y-3 pt-4">
            <article
              v-for="review in reviews"
              :key="review._id"
              class="rounded-[14px] border border-slate-200 bg-slate-50/60 p-4"
            >
              <div class="flex flex-col gap-4 md:flex-row md:items-start">
                <img
                  :src="getAvatarUrl(review)"
                  :alt="review.fullName"
                  class="h-14 w-14 flex-shrink-0 rounded-full border border-[#D4AF37]/40 object-cover"
                >

                <div class="min-w-0 flex-1">
                  <div class="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <div class="min-w-0">
                      <h3 class="truncate text-sm font-semibold text-slate-900">{{ review.fullName }}</h3>
                      <p class="truncate text-xs text-slate-500">{{ review.profession || 'Client satisfait' }}</p>
                    </div>
                    <p class="text-xs text-slate-400">{{ formatDate(review.createdAt) }}</p>
                  </div>

                  <div class="mt-2 flex items-center gap-1 text-sm text-[#D4AF37]">
                    <span v-for="star in 5" :key="star" :class="star <= review.rating ? 'opacity-100' : 'opacity-25'">★</span>
                  </div>

                  <p class="mt-3 text-sm leading-7 text-slate-700">
                    {{ review.comment || 'Le client a laissé une note sans commentaire détaillé.' }}
                  </p>

                  <div v-if="review.response?.message" class="mt-3 rounded-[12px] border border-[#D4AF37]/20 bg-[#fffaf2] p-3">
                    <p class="text-xs font-medium text-[#8f6a09]">Réponse envoyée</p>
                    <p class="mt-1 text-sm leading-7 text-slate-700">{{ review.response.message }}</p>
                  </div>

                  <div class="mt-4 flex flex-wrap items-center gap-2">
                    <button type="button" class="btn-secondary" @click="openReplyModal(review)">
                      {{ review.response?.message ? 'Modifier la réponse' : 'Répondre' }}
                    </button>

                    <button type="button" class="btn-danger" @click="deleteReview(review._id)">
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            </article>

            <div v-if="pagination.hasMore" class="flex justify-center pt-2">
              <button
                type="button"
                class="btn-load"
                :disabled="loadingMore"
                @click="fetchReviews({ append: true })"
              >
                <span v-if="loadingMore">Chargement...</span>
                <span v-else>Charger plus</span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  </main>

  <Transition name="reply-modal">
    <div
      v-if="activeReplyReview"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4"
      @click.self="closeReplyModal"
    >
      <div class="w-full max-w-[520px] rounded-[16px] bg-white p-5 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.45)]">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h3 class="text-base font-semibold text-slate-900">Répondre à l’avis</h3>
            <p class="mt-1 text-sm text-slate-500">{{ activeReplyReview.fullName }}</p>
          </div>

          <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:text-[#1f2937]"
            @click="closeReplyModal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="mt-4 rounded-[12px] bg-slate-50 p-3">
          <p class="text-sm leading-7 text-slate-700">
            {{ activeReplyReview.comment || 'Le client a laissé une note sans commentaire détaillé.' }}
          </p>
        </div>

        <div class="mt-4">
          <label class="mb-2 block text-sm font-medium text-slate-700">Votre réponse</label>
          <textarea
            v-model="replyMessage"
            rows="6"
            class="textarea"
            placeholder="Rédigez votre réponse"
          ></textarea>
        </div>

        <div class="mt-4 flex justify-end gap-2">
          <button type="button" class="btn-ghost" @click="closeReplyModal">Annuler</button>
          <button type="button" class="btn-primary" :disabled="replySubmitting" @click="submitReply">
            <span v-if="replySubmitting">Envoi...</span>
            <span v-else>Envoyer</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { toast } from 'vue3-toastify';
import apiClient from '@/services/api.ts';
import HeaderComponent from '@/components/HeaderComponentAdmin.vue';
import SideBarComponent from '@/components/SideBarComponent.vue';

const reviews = ref([]);
const loading = ref(false);
const loadingMore = ref(false);
const sendingRequest = ref(false);
const replySubmitting = ref(false);
const activeReplyReview = ref(null);
const replyMessage = ref('');
const form = ref({
  fullName: '',
  email: ''
});
const pagination = ref({
  page: 0,
  limit: 8,
  totalItems: 0,
  totalPages: 0,
  hasMore: true
});

const apiOrigin = (apiClient.defaults.baseURL || '').replace(/\/api\/v1\/?$/, '');

const mergeUniqueReviews = (existingReviews, incomingReviews) => {
  const byId = new Map(existingReviews.map((review) => [review._id, review]));

  incomingReviews.forEach((review) => {
    byId.set(review._id, review);
  });

  return Array.from(byId.values());
};

const fetchReviews = async ({ append = false } = {}) => {
  if ((append && loadingMore.value) || (!append && loading.value)) {
    return;
  }

  const nextPage = append ? (pagination.value.page || 0) + 1 : 1;

  if (append && !pagination.value.hasMore) {
    return;
  }

  if (append) {
    loadingMore.value = true;
  } else {
    loading.value = true;
  }

  try {
    const { data } = await apiClient.get('/reviews', {
      params: {
        page: nextPage,
        limit: 8
      }
    });

    const items = data?.items || [];

    reviews.value = append ? mergeUniqueReviews(reviews.value, items) : items;
    pagination.value = data?.pagination || {
      page: nextPage,
      limit: 8,
      totalItems: items.length,
      totalPages: items.length ? 1 : 0,
      hasMore: false
    };
  } catch (error) {
    toast.error('Erreur lors du chargement des avis.');
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

const sendRequest = async () => {
  sendingRequest.value = true;

  try {
    await apiClient.post('/reviews/request/manual', form.value);
    toast.success('Demande envoyée avec succès.');
    form.value.fullName = '';
    form.value.email = '';
  } catch (error) {
    toast.error(error.response?.data?.message || 'Erreur lors de l’envoi.');
  } finally {
    sendingRequest.value = false;
  }
};

const openReplyModal = (review) => {
  activeReplyReview.value = review;
  replyMessage.value = review.response?.message || '';
};

const closeReplyModal = () => {
  activeReplyReview.value = null;
  replyMessage.value = '';
};

const submitReply = async () => {
  const message = replyMessage.value.trim();

  if (!activeReplyReview.value) {
    return;
  }

  if (!message) {
    toast.error('Veuillez écrire une réponse avant l’envoi.');
    return;
  }

  replySubmitting.value = true;

  try {
    const { data } = await apiClient.patch(`/reviews/${activeReplyReview.value._id}/respond`, { message });
    reviews.value = reviews.value.map((review) => review._id === activeReplyReview.value._id ? data.review : review);
    toast.success(data.message || 'Réponse enregistrée avec succès.');
    closeReplyModal();
  } catch (error) {
    toast.error(error.response?.data?.message || 'Erreur lors de l’envoi de la réponse.');
  } finally {
    replySubmitting.value = false;
  }
};

const deleteReview = async (id) => {
  if (!confirm('Supprimer cet avis ?')) {
    return;
  }

  try {
    await apiClient.delete(`/reviews/${id}`);
    reviews.value = reviews.value.filter((review) => review._id !== id);
    pagination.value = {
      ...pagination.value,
      totalItems: Math.max((pagination.value.totalItems || 1) - 1, 0)
    };
    toast.success('Avis supprimé.');
  } catch (error) {
    toast.error(error.response?.data?.message || 'Erreur lors de la suppression.');
  }
};

const formatDate = (dateString) => {
  if (!dateString) {
    return '';
  }

  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getAvatarUrl = (review) => {
  return `${apiOrigin}${review?.image || '/Avis/default.png'}`;
};

onMounted(() => {
  fetchReviews();
});
</script>

<style scoped>
.input {
  @apply w-full rounded-[12px] border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-[#D4AF37] focus:ring-4 focus:ring-[#D4AF37]/10;
}

.textarea {
  @apply w-full rounded-[12px] border border-slate-200 bg-white px-4 py-3 text-sm leading-7 text-slate-800 outline-none transition focus:border-[#D4AF37] focus:ring-4 focus:ring-[#D4AF37]/10;
}

.btn-primary {
  @apply inline-flex items-center justify-center rounded-[12px] bg-[#D4AF37] px-4 py-2.5 text-sm font-medium text-slate-900 transition hover:bg-[#ddb84c] disabled:cursor-not-allowed disabled:opacity-70;
}

.btn-secondary {
  @apply inline-flex items-center justify-center rounded-[12px] border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-[#D4AF37] hover:text-[#8f6a09];
}

.btn-danger {
  @apply inline-flex items-center justify-center rounded-[12px] border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-500 transition hover:border-red-300 hover:bg-red-50;
}

.btn-ghost {
  @apply inline-flex items-center justify-center rounded-[12px] border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50;
}

.btn-load {
  @apply inline-flex items-center justify-center rounded-[12px] border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:border-[#D4AF37] hover:text-[#8f6a09] disabled:cursor-not-allowed disabled:opacity-70;
}

.reply-modal-enter-active,
.reply-modal-leave-active {
  transition: opacity 0.2s ease;
}

.reply-modal-enter-from,
.reply-modal-leave-to {
  opacity: 0;
}
</style>
